import { get, onValue, ref, update } from 'firebase/database';
import { ChevronLeft, ChevronRight, Clock, Eye } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../Database';
import { useUserContext } from '../contexts/user';
import { usePopUpContext } from '../contexts/Popup';
import { useLoaderContext } from '../contexts/loader';
import isArabic from 'is-arabic';

const SolveChallenge = () => {
    const navigate = useNavigate();
    const { id } = useParams();

    const [challengeData, setChallengeData] = useState({});
    const [remainingTime, setRemainingTime] = useState({ minutes: 0, seconds: 0, });
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [allStudents, setAllStudents] = useState([]);
    const [showUserResult, setShowUserResult] = useState({});

    const { setIsLoading } = useLoaderContext();
    const { userInfo, appLanguage } = useUserContext();
    const { setMessageContent } = usePopUpContext();

    const userAnswersRef = useRef();
    const answers = useRef([]);
    const questions = useRef([]);
    const userIndex = useRef(-1);

    useEffect(() => {
        if (id && userInfo.id) {
            setIsLoading(prev => prev + 1);
            get(ref(db, "challenges/" + id)).then((snapShot) => {
                const data = snapShot.val();
                if (data) {
                    if (data.creator_id === userInfo.id || data.users?.find(user => user.user_id === userInfo.id)) {
                        if (data.status === "solving") {
                            if (data.creator_id !== userInfo.id && data.users?.find(user => user.user_id === userInfo.id).finished) {
                                navigate("/challenge-result/" + id);
                            } else {
                                answers.current = data.users?.find(user => user.user_id === userInfo.id)?.answers ? data.users?.find(user => user.user_id === userInfo.id)?.answers : [];
                                questions.current = data.questions;
                                userIndex.current = data.users?.findIndex(user => user.user_id === userInfo.id);
                                setChallengeData({
                                    ...data,
                                    questions: data.questions.map((question) => {
                                        const answers = [...question.answers];
                                        const newAnswers = [];
                                        while (answers.length !== 0) {
                                            const randomAnswer = Math.floor(Math.random() * answers.length);
                                            newAnswers.push(answers[randomAnswer]);
                                            answers.splice(randomAnswer, 1);
                                        }
                                        return { ...question, answers: newAnswers, }
                                    })
                                });
                                let remainingDifference = data.challengeEndsAt - new Date().getTime();
                                let interval = setInterval(() => {
                                    remainingDifference = data.challengeEndsAt - new Date().getTime();
                                    if (remainingDifference > 0) {
                                        setRemainingTime({
                                            minutes: Math.floor(remainingDifference / 1000 / 60),
                                            seconds: Math.floor(remainingDifference / 1000 % 60),
                                        })
                                    } else {
                                        clearInterval(interval);
                                        setRemainingTime({
                                            minutes: 0,
                                            seconds: 0,
                                        })
                                        if(userInfo.id !== data.creator_id) {
                                            finishChallenge();
                                        }
                                    }
                                }, 1000)
                            }
                        } else {
                            navigate("/challenge/" + id);
                        }
                    } else {
                        navigate("/live-challenge/join");
                        setMessageContent({
                            title: appLanguage === "ar" ? "تم رفض الوصول" : "Access Denied",
                            message: appLanguage === "ar" ? "يمكن للطلاب فقط في هذا التحدي والمعلم الوصول إلى هذه الصفحة." : "Only students in this challenge and the teacher can access this page.",
                        })

                    }
                } else {
                    navigate("/home");
                    setMessageContent({
                        title: appLanguage === "ar" ? "لم يتم العثور على التحدي" : "Challenge Not Found",
                        message: appLanguage === "ar" ? "التحدي غير موجود في قاعدة بياناتنا." : "Challenge is not found in our database.",
                    })
                }
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            }).catch((err) => {
                console.log(err);
                navigate("/challenge/" + id);
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في الجلب" : "Fetching Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء جلب بيانات التحدي." : "Oops! something went wrong while fetching challenge data.",
                })
            })
        }
    }, [userInfo])

    useEffect(() => {
        if (challengeData.challenge_code && userInfo.id === challengeData.creator_id) {
            const userFinished = (snapShot) => {
                const allUsers = snapShot.val();
                allUsers?.sort((a, b) => (b.points || 0) - (a.points || 0));
                setAllStudents(allUsers);
            }

            onValue(ref(db, "challenges/" + id + "/users"), userFinished);
        }
    }, [challengeData])

    const changeAnswer = (i) => {
        const newAnswer = challengeData.questions[currentQuestionIndex].answers[i];
        const userIndex = challengeData.users.findIndex(user => user.user_id === userInfo.id);
        const userAnswers = challengeData.users[userIndex].answers ? [...challengeData.users[userIndex].answers] : [];
        userAnswers[currentQuestionIndex] = newAnswer;
        for (let i = 0; i < userAnswers.length; i++) {
            if (userAnswers[i] === undefined) {
                userAnswers[i] = { empty: true, };
            }
        }
        const newUsersArray = [...challengeData.users];
        newUsersArray[userIndex].answers = userAnswers;
        answers.current = userAnswers;
        setChallengeData(prev => ({
            ...prev,
            users: newUsersArray,
        }))
        update(ref(db, "challenges/" + id + "/users/" + userIndex), {
            answers: userAnswers,
        }).then(() => {
            answers.current = userAnswers;
            setChallengeData(prev => ({
                ...prev,
                users: newUsersArray,
            }))
        }).catch(() => {
            console.log("Oops! something went wrong while updating user answer.");
        })
    }

    const hideContainer = (e) => {
        const { clientX, clientY } = e;
        const contentRect = userAnswersRef.current.getBoundingClientRect();
        if (
            clientX > contentRect.right ||
            clientX < contentRect.left ||
            clientY > contentRect.bottom ||
            clientY < contentRect.top
        ) {
            setShowUserResult({});
        }
    }

    const selectPercentageColor = (percentage) => {
        if (percentage >= 80) return "#0b0"
        else if (percentage >= 50) return "#f90"
        else return "#f00";
    }

    const finishChallenge = () => {
        if (userInfo.id !== challengeData.creator_id) {
            setIsLoading(prev => prev + 1);
            let points = 0;
            let userAnswers = answers.current.length ? answers.current : [{empty: true,}];
            for (let i = 0; i < questions.current?.length; i++) {
                if (!userAnswers[i]) {
                    userAnswers[i] = { empty: true, };
                }
            }
            for (let i = 0; i < questions.current.length; i++) {
                if (questions.current[i].correct_answer === userAnswers[i]) {
                    points += 5;
                }
            }
            update(ref(db, "challenges/" + id + "/users/" + userIndex.current), {
                points: points,
                finished: true,
                answers: userAnswers,
            }).then(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                navigate("/challenge-result/" + id);
            })
        }
    }

    const goToHome = () => {
        if (remainingTime.minutes < 1 && remainingTime.seconds < 1 || challengeData.users.filter(user => !user.finished).length === 0) {
            setIsLoading(prev => prev + 1);
            update(ref(db, "challenges/" + id), {
                challengeEndsAt: "",
                users: [
                    ...challengeData.users.map(user => ({
                        ...user,
                        answers: [],
                        finished: false,
                    }))
                ],
                status: "waiting",
            }).then(() => {
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
                navigate("/challenge/" + id);
            }).catch(() => {
                setMessageContent({
                    title: appLanguage === "ar" ? "خطأ في التنقل" : "Navigating Error",
                    message: appLanguage === "ar" ? "عذراً! حدث خطأ ما أثناء الانتقال إلى الصفحة الرئيسية" : "Oops! something went wrong while going to the main page",
                })
                setIsLoading(prev => prev !== 0 ? prev - 1 : 0);
            })
        } else {
            setMessageContent({
                title: appLanguage === "ar" ? "لم ينته بعد" : "Not Finished",
                message: appLanguage === "ar" ? "لا يمكنك الذهاب إلى الصفحة الرئيسية إذا كان هناك بعض اللاعبين لم ينتهوا أو لم ينته الوقت." : "You can't go the main page if there is some players didn't finish or time didn't end.",
            })
        }
    }

    return (
        <div className="solve_challenge_container">
            <header>
                <p className="title">{challengeData.creator_id === userInfo.id ? (appLanguage === "ar" ? "صفحة الانتظار" : "Waiting Page") : (appLanguage === "ar" ? "حل التحدي" : "Solve Challenge")}</p>
                <div className="timer">
                    <Clock />
                    <p>{remainingTime.minutes < 10 ? "0" + remainingTime.minutes : remainingTime.minutes}:{remainingTime.seconds < 10 ? "0" + remainingTime.seconds : remainingTime.seconds}</p>
                </div>
            </header>
            {
                challengeData.creator_id === userInfo.id ?
                    <div className="waiting_container">
                        <div className="main_content">
                            <div className="remaining_time_container">
                                <p className="title">{appLanguage === "ar" ? "الوقت المتبقي" : "Remaining Time"}</p>
                                <div className="remaining_time">
                                    <div className="minutes_content">
                                        <p className="minutes">{remainingTime.minutes < 10 && remainingTime.minutes > -1 ? "0" + remainingTime.minutes : remainingTime.minutes}</p>
                                        <p className="text">{appLanguage === "ar" ? "دقائق" : "Minutes"}</p>
                                    </div>
                                    <div className="seconds_content">
                                        <p className="minutes">{remainingTime.seconds < 10 && remainingTime.seconds > -1 ? "0" + remainingTime.seconds : remainingTime.seconds}</p>
                                        <p className="text">{appLanguage === "ar" ? "ثواني" : "Seconds"}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="finished_students">
                                <p className="title">{appLanguage === "ar" ? "الطلاب الذين انتهوا" : "Students That Finished"}</p>
                                <div className="students">
                                    <div className="finished_students_content">
                                        <div className="header">
                                            <span></span>
                                            <p>{appLanguage === "ar" ? "انتهى" : "Finished"}</p>
                                            <span></span>
                                        </div>
                                        <div className="content">
                                            {allStudents.filter(student => student.finished === true).length ?
                                                allStudents.filter(student => student.finished === true).map((student, i) => {
                                                    return <div className="student" key={"student_" + i}>
                                                        <div className="left_side">
                                                            <p className="rank">#{i + 1}</p>
                                                            <p className="username">{student.username}</p>
                                                        </div>
                                                        <div className="right_side">
                                                            <p className="percentage" style={{ color: selectPercentageColor(student.points ? (Math.round(student.points / (challengeData.questions.length * 5) * 100)) : 0), }}>{student.points ? Math.round(student.points / (challengeData.questions.length * 5) * 100) : 0}%</p>
                                                            <button onClick={() => setShowUserResult(student)}><Eye /></button>
                                                        </div>
                                                    </div>
                                                })
                                                :
                                                <p className="no_students">{appLanguage === "ar" ? "لم ينته أي طالب بعد" : "No Students Finished"}</p>
                                            }
                                        </div>
                                    </div>
                                    <div className="remaining_students">
                                        <div className="header">
                                            <span></span>
                                            <p>{appLanguage === "ar" ? "المتبقي" : "Remaining"}</p>
                                            <span></span>
                                        </div>
                                        <div className="content">
                                            {
                                                allStudents.filter(student => !student.finished).map((student, i) => {
                                                    return <div className="student" key={"student_" + i}>
                                                        <div className="left_side">
                                                            <p className="username">{student.username}</p>
                                                        </div>
                                                        <div className="right_side"></div>
                                                    </div>
                                                })
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <button className="main_page" onClick={goToHome}>{appLanguage === "ar" ? "الصفحة الرئيسية" : "Main Page"}</button>
                    </div>
                    :
                    <>
                        <div className="content">
                            <div className="question_container">
                                <div className="question_text" dir={challengeData.questions ? (isArabic(challengeData.questions[currentQuestionIndex].text.split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{challengeData.questions ? challengeData.questions[currentQuestionIndex].text : (appLanguage === "ar" ? "جاري جلب بيانات التحدي..." : "Fetching Challenge Data...")}</div>
                                <div className="answers">
                                    <button onClick={() => changeAnswer(0)} className={challengeData.challenge_code && challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)] ? ((challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)].answers ? challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)]?.answers[currentQuestionIndex] : "") === challengeData.questions[currentQuestionIndex].answers[0] ? "active" : "") : ""} dir={challengeData.questions ? (isArabic(challengeData.questions[currentQuestionIndex].answers[0].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{challengeData.questions ? challengeData.questions[currentQuestionIndex].answers[0] : (appLanguage === "ar" ? "جاري جلب البيانات..." : "Fetching Data...")}</button>
                                    <button onClick={() => changeAnswer(1)} className={challengeData.challenge_code && challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)] ? ((challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)].answers ? challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)]?.answers[currentQuestionIndex] : "") === challengeData.questions[currentQuestionIndex].answers[1] ? "active" : "") : ""} dir={challengeData.questions ? (isArabic(challengeData.questions[currentQuestionIndex].answers[1].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{challengeData.questions ? challengeData.questions[currentQuestionIndex].answers[1] : (appLanguage === "ar" ? "جاري جلب البيانات..." : "Fetching Data...")}</button>
                                    <button onClick={() => changeAnswer(2)} className={challengeData.challenge_code && challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)] ? ((challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)].answers ? challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)]?.answers[currentQuestionIndex] : "") === challengeData.questions[currentQuestionIndex].answers[2] ? "active" : "") : ""} dir={challengeData.questions ? (isArabic(challengeData.questions[currentQuestionIndex].answers[2].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{challengeData.questions ? challengeData.questions[currentQuestionIndex].answers[2] : (appLanguage === "ar" ? "جاري جلب البيانات..." : "Fetching Data...")}</button>
                                    <button onClick={() => changeAnswer(3)} className={challengeData.challenge_code && challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)] ? ((challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)].answers ? challengeData.users[challengeData.users.findIndex(user => user.user_id === userInfo.id)]?.answers[currentQuestionIndex] : "") === challengeData.questions[currentQuestionIndex].answers[3] ? "active" : "") : ""} dir={challengeData.questions ? (isArabic(challengeData.questions[currentQuestionIndex].answers[3].split(" ")[0], { count: 0, }) ? "rtl" : "ltr") : "ltr"}>{challengeData.questions ? challengeData.questions[currentQuestionIndex].answers[3] : (appLanguage === "ar" ? "جاري جلب البيانات..." : "Fetching Data...")}</button>
                                </div>
                            </div>
                        </div>
                        <div className="questions_navigation">
                            <button className={currentQuestionIndex === 0 ? "disabled" : ""} onClick={() => setCurrentQuestionIndex(prev => prev !== 0 ? prev - 1 : 0)}><ChevronLeft /> {appLanguage === "ar" ? "السابق" : "Back"}</button>
                            <div className="question_index">
                                <p>{currentQuestionIndex + 1 < 10 ? "0" + (currentQuestionIndex + 1) : (currentQuestionIndex + 1)}/{challengeData.questions?.length < 10 ? "0" + challengeData.questions?.length : challengeData.questions?.length}</p>
                            </div>
                            <button onClick={() => { currentQuestionIndex + 1 === challengeData.questions.length ? finishChallenge() : setCurrentQuestionIndex(prev => prev + 1) }}>{currentQuestionIndex + 1 === challengeData.questions?.length ? (appLanguage === "ar" ? "إنهاء" : "Finish") : <>{appLanguage === "ar" ? "التالي" : "Next"} <ChevronRight /></>}</button>
                        </div>
                    </>
            }
            {
                showUserResult.user_id ?
                    <div className="student_answers" onClick={hideContainer}>
                        <div className="content" ref={userAnswersRef}>
                            <div className="head">
                                <p className="username">{showUserResult.username} {appLanguage === "ar" ? "(الإجابات)" : "(Answers)"}</p>
                            </div>
                            <div className="body">
                                {
                                    challengeData.questions?.map((question, i) => {
                                        return <div className="question" key={"question_" + i}>
                                            <p className="question_text">{question.text}</p>
                                            <button className={showUserResult.answers ? (showUserResult.answers[i] === question.correct_answer ? "correct" : "wrong") : "wrong"}>{!showUserResult.answers[i]["empty"] && showUserResult.answers ? (showUserResult.answers[i] ? showUserResult.answers[i] : question.correct_answer) : question.correct_answer}</button>
                                        </div>
                                    })
                                }
                            </div>
                        </div>
                    </div> : undefined
            }
        </div>
    )
}

export default SolveChallenge